import { api } from "./api";
import { h, clear, replace } from "./dom";
import { count, formatDate, lifespan, personName } from "./format";
import { icon } from "./icons";
import { lightboxOpen, openAsset } from "./lightbox";
import { store } from "./state";
import type { AssetCard, PersonPage, TreeNode } from "./types";
import { face, loading, tile } from "./ui";

const PAGE = 60;

let host: HTMLElement | null = null;
let onKey: ((event: KeyboardEvent) => void) | null = null;

export function closePersonGallery(): void {
  host?.remove();
  host = null;
  if (onKey) document.removeEventListener("keydown", onKey, true);
  onKey = null;
}

function profile(person: PersonPage, kinship: string | null, onReroot: () => void): HTMLElement {
  const chosen = person.crops.find((crop) => crop.chosen) ?? person.crops[0];
  const facts = h("dl", { class: "kv person-gallery-facts" });
  const fact = (label: string, value: string): void => {
    facts.append(h("dt", { text: label }), h("dd", { text: value }));
  };
  fact("Life", lifespan(person.birthYear, person.deathYear));
  fact("In the archive", `${count(person.photoCount, "photograph")} · ${count(person.videoCount, "film")}`);
  if (person.firstSeen || person.lastSeen) {
    fact("Seen", `${formatDate(person.firstSeen, null, { short: true })} to ${formatDate(person.lastSeen, null, { short: true })}`);
  }
  if (kinship) fact("Relationship", kinship);

  const relatives = person.relatives.slice(0, 5);
  const stories = person.stories.slice(0, 3);
  return h(
    "aside",
    { class: "person-gallery-profile" },
    h(
      "div",
      { class: "person-gallery-identity" },
      face(person.name, chosen?.cropKey ?? null, "person-gallery-face"),
      h("div", null, h("h2", { text: personName(person.name) }), kinship ? h("p", { class: "sub", text: kinship }) : null),
    ),
    facts,
    stories.length
      ? h(
          "section",
          { class: "person-gallery-notes" },
          h("h3", { text: "Remembered" }),
          ...stories.map((story) => h("p", { text: story.body ?? "Audio memory" })),
        )
      : null,
    relatives.length
      ? h(
          "section",
          { class: "person-gallery-relatives" },
          h("h3", { text: "Family" }),
          ...relatives.map((relative) =>
            h("div", null, h("strong", { text: personName(relative.name) }), h("span", { text: relative.kinship })),
          ),
        )
      : null,
    h("div", { class: "person-gallery-profile-actions" },
      h("button", { class: "btn", text: "Center tree here", onclick: onReroot }),
      h("button", {
        class: "btn ghost",
        text: "Full person page",
        onclick: () => {
          closePersonGallery();
          store.go("people", { personId: person.identityId });
        },
      }),
    ),
  );
}

export function openPersonGallery(node: TreeNode, onReroot: () => void): void {
  closePersonGallery();

  const profileHost = h("div", { class: "person-gallery-profile-host" }, loading("person"));
  const gallery = h("div", { class: "person-gallery-grid" });
  const status = h("div", { class: "person-gallery-status", text: "Loading artifacts..." });
  const title = h("h2", { text: `Artifacts with ${personName(node.name)}` });
  const scroller = h("section", { class: "person-gallery-content" },
    h("header", null, title, h("p", { class: "sub", text: "Photographs and film, newest first." })),
    gallery,
    status,
  );
  const panel = h(
    "div",
    { class: "person-gallery glass", role: "dialog", "aria-modal": "true", "aria-label": `Artifacts with ${personName(node.name)}` },
    profileHost,
    scroller,
    h("button", { class: "ib person-gallery-close", title: "Close", "aria-label": "Close", onclick: closePersonGallery }, icon("x")),
  );
  const thisHost = h("div", {
    class: "person-gallery-scrim",
    onclick: (event: MouseEvent) => {
      if (event.target === thisHost) closePersonGallery();
    },
  }, panel);
  host = thisHost;
  document.body.appendChild(thisHost);

  let items: AssetCard[] = [];
  let cursor: string | null = null;
  let total: number | null = null;
  let fetching = false;
  let finished = false;

  const renderArtifacts = (): void => {
    clear(gallery);
    for (const asset of items) {
      const card = tile(asset, openAsset);
      if (asset.width && asset.height) {
        const rotated = asset.orientation >= 5 && asset.orientation <= 8;
        card.style.setProperty("--asset-ratio", rotated
          ? `${asset.height} / ${asset.width}`
          : `${asset.width} / ${asset.height}`);
      }
      gallery.appendChild(card);
    }
    status.textContent = fetching
      ? "Loading more..."
      : finished
        ? items.length ? `All ${count(items.length, "artifact")}` : "No artifacts found"
        : `${count(items.length, "artifact")} shown`;
    title.textContent = `${total === null ? "Artifacts" : count(total, "artifact")} with ${personName(node.name)}`;
  };

  const more = async (): Promise<void> => {
    if (fetching || finished || host !== thisHost) return;
    fetching = true;
    renderArtifacts();
    try {
      const page = await api.cards(store.libraryId, { people: [node.identityId] }, { limit: PAGE, cursor: cursor ?? undefined });
      if (host !== thisHost) return;
      items = items.concat(page.items);
      cursor = page.nextCursor;
      total = page.total;
      finished = !page.nextCursor;
    } catch (error) {
      if (host !== thisHost) return;
      finished = true;
      status.textContent = error instanceof Error ? error.message : "Could not load artifacts";
    } finally {
      fetching = false;
      if (host === thisHost) renderArtifacts();
    }
  };

  scroller.addEventListener("scroll", () => {
    if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 500) void more();
  }, { passive: true });

  void api.person(store.libraryId, node.identityId)
    .then((person) => {
      if (host !== thisHost) return;
      replace(profileHost, profile(person, node.kinship, () => {
        closePersonGallery();
        onReroot();
      }));
    })
    .catch((error: unknown) => {
      if (host !== thisHost) return;
      replace(profileHost, h("p", { class: "sub", text: error instanceof Error ? error.message : "Could not load this person" }));
    });
  void more();

  onKey = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !lightboxOpen()) closePersonGallery();
  };
  document.addEventListener("keydown", onKey, true);
}
